import { Component } from '@theme/component';

/**
 * Count label from the element's templates ("1 peak" / "12 peaks").
 * @param {HTMLElement} element
 * @param {number} count
 */
function countLabel(element, count) {
  const template = (count === 1 ? element.dataset.countOneTemplate : null) ?? element.dataset.countTemplate ?? '';
  return template.replace('[count]', String(count));
}

/** @type {Map<string, Promise<string>>} One request per section URL, shared by every instance. */
const peakListRequests = new Map();

/**
 * Fetches the peak list markup once and caches the in-flight or settled promise.
 * A failed request is dropped from the cache so the next intent can retry.
 * @param {string} url - Section Rendering API URL for `sections/peak-index.liquid`.
 * @returns {Promise<string>} The inner HTML of the peak list.
 */
function loadPeakList(url) {
  let request = peakListRequests.get(url);
  if (request) return request;

  request = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Peak list request failed: ${response.status}`);
      return response.text();
    })
    .then((html) => {
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-peak-index]');
      if (!list) throw new Error('Peak list markup missing');
      return list.innerHTML;
    });

  request.catch(() => peakListRequests.delete(url));
  peakListRequests.set(url, request);
  return request;
}

/**
 * Header peak dropdown. The list loads on intent (hover, touch, focus) so it is usually ready
 * by the time the popover opens; a skeleton shows only while the request is still pending.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle - Disclosure button.
 * @property {HTMLElement} popover - Popover panel.
 * @property {HTMLUListElement} list - Peak list.
 * @property {HTMLElement} count - Peak count label.
 * @property {HTMLElement} error - Error message.
 * @property {HTMLLabelElement} [search] - Filter field wrapper (shown once the list loads).
 * @property {HTMLInputElement} [input] - Filter input.
 * @property {HTMLElement} [empty] - "No peaks found" message.
 *
 * @extends {Component<Refs>}
 */
class PeakMenuComponent extends Component {
  requiredRefs = ['toggle', 'popover', 'list', 'count', 'error'];

  /** @type {AbortController | null} */
  #listeners = null;
  #loaded = false;

  /** @type {number | null} */
  #closeTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const item = this.closest('li') ?? this;

    item.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    item.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    item.addEventListener('touchstart', this.prefetch, { signal, passive: true });
    item.addEventListener('focusin', this.prefetch, { signal });
    item.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#cancelClose();
  }

  get isOpen() {
    return this.refs.toggle.getAttribute('aria-expanded') === 'true';
  }

  /** Starts loading the peak list without opening the popover. */
  prefetch = () => {
    if (this.#loaded) return;
    this.#render();
  };

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.refs.popover.hidden = false;
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    this.prefetch();
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   */
  close({ restoreFocus = false } = {}) {
    if (!this.isOpen) return;
    this.refs.popover.hidden = true;
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.refs.toggle.focus();
  }

  /** Shows only the peaks whose name or alias contains the filter text, and updates the count. */
  filter() {
    if (!this.#loaded) return;
    const query = this.refs.input?.value.trim().toLowerCase() ?? '';
    let shown = 0;
    for (const item of this.refs.list.querySelectorAll('li')) {
      const isMatch = !query || (item.dataset.search ?? '').includes(query);
      item.hidden = !isMatch;
      if (isMatch) shown += 1;
    }
    if (this.refs.empty) this.refs.empty.hidden = shown > 0;
    this.refs.count.textContent = countLabel(this, shown);
  }

  async #render() {
    const url = this.dataset.sectionUrl;
    if (!url) return;

    this.refs.error.hidden = true;

    try {
      const markup = await loadPeakList(url);
      if (!this.isConnected) return;

      this.refs.list.innerHTML = markup;
      this.refs.list.setAttribute('aria-busy', 'false');
      this.#loaded = true;

      if (this.refs.search) this.refs.search.hidden = false;
      this.filter();
    } catch (error) {
      if (!this.isConnected) return;
      this.refs.list.replaceChildren();
      this.refs.list.setAttribute('aria-busy', 'false');
      this.refs.error.hidden = false;
      console.warn('[peak-menu]', error);
    }
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#cancelClose();
    this.open();
  };

  /**
   * Closes shortly after the pointer leaves, so a slightly off path between the link and the
   * popover doesn't close it; coming back cancels.
   * @param {PointerEvent} event
   */
  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (this.contains(document.activeElement)) return;
    this.#cancelClose();
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null;
      this.close();
    }, 150);
  };

  #cancelClose() {
    if (this.#closeTimer === null) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    const item = this.closest('li') ?? this;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    this.close({ restoreFocus: true });
  };

  /** @param {PointerEvent} event */
  #handleDocumentPointerDown = (event) => {
    if (!this.isOpen) return;
    const item = this.closest('li') ?? this;
    if (event.target instanceof Node && item.contains(event.target)) return;
    this.close();
  };
}

if (!customElements.get('peak-menu-component')) {
  customElements.define('peak-menu-component', PeakMenuComponent);
}
